import { useState } from 'react'
import {
  Grid,
  Box,
  Typography,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  Divider,
} from '@mui/material'
import {
  Assessment,
  Build,
  Inventory2,
  AttachMoney,
  Assignment,
  LocalShipping,
  Download,
  Refresh,
  Timeline,
  AccountTree,
  Speed,
  Schedule,
  TrendingDown,
  Science,
  Biotech,
  EventNote,
} from '@mui/icons-material'
import { PageHeader, Card, Button, StatusChip, LoadingSpinner } from '@components/common'
import { toast } from 'react-toastify'
import api from '@services/api'

const reportTypes = [
  // Standard Reports
  {
    id: 'asset_summary',
    title: 'Asset Summary',
    description: 'Overview of all assets by status, category, and department',
    icon: <Assessment />,
    color: '#1B5E7A',
    endpoint: '/reports/data/asset_summary/',
    category: 'standard',
  },
  {
    id: 'maintenance_summary',
    title: 'Maintenance Report',
    description: 'Work order completion rates and maintenance metrics',
    icon: <Build />,
    color: '#10b981',
    endpoint: '/reports/data/maintenance_summary/',
    category: 'standard',
  },
  {
    id: 'work_order_analysis',
    title: 'Work Order Analysis',
    description: 'Detailed analysis of work orders by type and priority',
    icon: <Assignment />,
    color: '#3b82f6',
    endpoint: '/reports/data/work_order_analysis/',
    category: 'standard',
  },
  {
    id: 'inventory_summary',
    title: 'Inventory Status',
    description: 'Stock levels, usage trends, and reorder alerts',
    icon: <Inventory2 />,
    color: '#f59e0b',
    endpoint: '/reports/data/inventory_summary/',
    category: 'standard',
  },
  {
    id: 'cost_analysis',
    title: 'Cost Analysis',
    description: 'Maintenance costs breakdown by asset and category',
    icon: <AttachMoney />,
    color: '#ef4444',
    endpoint: '/reports/data/cost_analysis/',
    category: 'standard',
  },
  {
    id: 'vendor_performance',
    title: 'Vendor Performance',
    description: 'Vendor service ratings and contract compliance',
    icon: <LocalShipping />,
    color: '#8b5cf6',
    endpoint: '/reports/data/vendor_performance/',
    category: 'standard',
  },
  // Specialized Reports
  {
    id: 'downtime_analysis',
    title: 'Equipment Downtime',
    description: 'Downtime analysis by asset, department and category',
    icon: <TrendingDown />,
    color: '#dc2626',
    endpoint: '/reports/data/downtime_analysis/',
    category: 'specialized',
  },
  {
    id: 'pm_compliance',
    title: 'PM Compliance',
    description: 'Preventive maintenance completion rates and overdue tasks',
    icon: <Schedule />,
    color: '#0891b2',
    endpoint: '/reports/data/pm_compliance/',
    category: 'specialized',
  },
  {
    id: 'calibration_report',
    title: 'Calibration Report',
    description: 'Calibration records, compliance, and upcoming due dates',
    icon: <Speed />,
    color: '#7c3aed',
    endpoint: '/reports/data/calibration_report/',
    category: 'specialized',
  },
  {
    id: 'equipment_lifecycle',
    title: 'Equipment Lifecycle',
    description: 'Asset age analysis, replacement forecasts and warranties',
    icon: <Timeline />,
    color: '#059669',
    endpoint: '/reports/data/equipment_lifecycle/',
    category: 'specialized',
  },
  {
    id: 'departmental_costs',
    title: 'Departmental Costs',
    description: 'Maintenance costs broken down by department',
    icon: <AccountTree />,
    color: '#d97706',
    endpoint: '/reports/data/departmental_costs/',
    category: 'specialized',
  },
  {
    id: 'high_cost_assets',
    title: 'High Cost Assets',
    description: 'Assets with highest purchase and maintenance costs',
    icon: <AttachMoney />,
    color: '#be185d',
    endpoint: '/reports/data/high_cost_assets/',
    category: 'specialized',
  },
  // Radiation Oncology Reports
  {
    id: 'qa_compliance',
    title: 'QA Compliance',
    description: 'Radiation equipment QA records and pass rates',
    icon: <Science />,
    color: '#4f46e5',
    endpoint: '/reports/data/qa_compliance/',
    category: 'radiation',
  },
  {
    id: 'radiation_source_inventory',
    title: 'Source Inventory',
    description: 'Radiation source activity, decay tracking and leak tests',
    icon: <Biotech />,
    color: '#0d9488',
    endpoint: '/reports/data/radiation_source_inventory/',
    category: 'radiation',
  },
  {
    id: 'equipment_log_summary',
    title: 'Equipment Logs',
    description: 'LINAC beam time, downtime and treatment interruptions',
    icon: <EventNote />,
    color: '#ea580c',
    endpoint: '/reports/data/equipment_log_summary/',
    category: 'radiation',
  },
]

const StatBox = ({ label, value, color = 'text.primary' }) => (
  <Box textAlign="center" p={2}>
    <Typography variant="h4" fontWeight={700} color={color}>
      {value}
    </Typography>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
  </Box>
)

const DataBreakdown = ({ title, data, labelKey, valueKey = 'count' }) => {
  if (!data || data.length === 0) return null

  const total = data.reduce((sum, item) => sum + (item[valueKey] || 0), 0)

  return (
    <Box mt={3}>
      <Typography variant="subtitle2" fontWeight={600} mb={2}>
        {title}
      </Typography>
      {data.map((item, index) => {
        const label = item[labelKey] || 'Unknown'
        const value = item[valueKey] || 0
        const percentage = total > 0 ? (value / total) * 100 : 0

        return (
          <Box key={index} mb={1.5}>
            <Box display="flex" justifyContent="space-between" mb={0.5}>
              <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                {String(label).replace(/_/g, ' ')}
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {value} ({percentage.toFixed(1)}%)
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={percentage}
              sx={{
                height: 6,
                borderRadius: 1,
                backgroundColor: '#e2e8f0',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: '#1B5E7A',
                },
              }}
            />
          </Box>
        )
      })}
    </Box>
  )
}

const ReportsList = () => {
  const [selectedReport, setSelectedReport] = useState(null)
  const [reportData, setReportData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [days, setDays] = useState(30)

  const handleGenerateReport = async (report) => {
    setSelectedReport(report)
    setLoading(true)
    setError(null)
    setReportData(null)

    try {
      const params = ['maintenance_summary', 'work_order_analysis', 'cost_analysis'].includes(report.id)
        ? { days }
        : {}
      const response = await api.get(report.endpoint, { params })
      setReportData(response.data)
    } catch (err) {
      console.error('Error generating report:', err)
      setError('Failed to generate report. Please try again.')
      toast.error('Failed to generate report')
    } finally {
      setLoading(false)
    }
  }

  const handleExportCSV = () => {
    if (!reportData || !selectedReport) return

    let csvContent = ''
    const timestamp = new Date().toISOString().split('T')[0]

    switch (selectedReport.id) {
      case 'asset_summary':
        csvContent = 'Asset Summary Report\n\n'
        csvContent += `Total Assets,${reportData.total}\n\n`
        csvContent += 'Status,Count\n'
        reportData.by_status?.forEach((item) => {
          csvContent += `${item.status || 'Unknown'},${item.count}\n`
        })
        csvContent += '\nCategory,Count\n'
        reportData.by_category?.forEach((item) => {
          csvContent += `${item.category__name || 'Unknown'},${item.count}\n`
        })
        break

      case 'maintenance_summary':
      case 'work_order_analysis':
        csvContent = `${selectedReport.title} Report (Last ${reportData.period_days} days)\n\n`
        csvContent += `Total Work Orders,${reportData.total_work_orders}\n`
        if (reportData.completion_rate !== undefined) {
          csvContent += `Completion Rate,${reportData.completion_rate}%\n`
        }
        csvContent += '\nStatus,Count\n'
        reportData.by_status?.forEach((item) => {
          csvContent += `${item.status || 'Unknown'},${item.count}\n`
        })
        csvContent += '\nPriority,Count\n'
        reportData.by_priority?.forEach((item) => {
          csvContent += `${item.priority || 'Unknown'},${item.count}\n`
        })
        break

      case 'inventory_summary':
        csvContent = 'Inventory Summary Report\n\n'
        csvContent += `Total Parts,${reportData.total_parts}\n`
        csvContent += `Low Stock Items,${reportData.low_stock_count}\n`
        csvContent += `Total Inventory Value,${reportData.total_inventory_value}\n`
        break

      case 'cost_analysis':
        csvContent = `Cost Analysis Report (Last ${reportData.period_days} days)\n\n`
        csvContent += `Total Maintenance Cost,${reportData.total_maintenance_cost}\n`
        csvContent += `Average Work Order Cost,${reportData.average_work_order_cost}\n`
        csvContent += '\nWork Type,Total Cost,Count\n'
        reportData.by_work_type?.forEach((item) => {
          csvContent += `${item.work_type || 'Unknown'},${item.total || 0},${item.count}\n`
        })
        break

      case 'vendor_performance':
        csvContent = 'Vendor Performance Report\n\n'
        csvContent += `Total Active Vendors,${reportData.total_vendors}\n`
        csvContent += `Active Contracts,${reportData.active_contracts}\n`
        csvContent += `Expiring Soon,${reportData.expiring_soon}\n`
        csvContent += '\nTop Vendors\nName,Type,Rating,Contracts\n'
        reportData.top_vendors?.forEach((v) => {
          csvContent += `${v.name},${v.vendor_type},${v.rating},${v.contracts_count}\n`
        })
        break

      default:
        csvContent = JSON.stringify(reportData, null, 2)
    }

    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${selectedReport.id}_${timestamp}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
  }

  const renderReportContent = () => {
    if (!selectedReport) return null

    if (loading) {
      return <LoadingSpinner message="Generating report..." />
    }

    if (error) {
      return (
        <Box textAlign="center" py={4}>
          <Typography color="error" mb={2}>{error}</Typography>
          <Button onClick={() => handleGenerateReport(selectedReport)}>
            Try Again
          </Button>
        </Box>
      )
    }

    if (!reportData) return null

    switch (selectedReport.id) {
      case 'asset_summary':
        return (
          <>
            <Grid container spacing={2} mb={3}>
              <Grid item xs={12} sm={4}>
                <StatBox label="Total Assets" value={reportData.total} color="#1B5E7A" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <StatBox
                  label="Operational"
                  value={reportData.by_status?.find((s) => s.status === 'operational')?.count || 0}
                  color="#10b981"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <StatBox
                  label="Under Maintenance"
                  value={reportData.by_status?.find((s) => s.status === 'under_maintenance')?.count || 0}
                  color="#f59e0b"
                />
              </Grid>
            </Grid>
            <Divider />
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <DataBreakdown title="By Status" data={reportData.by_status} labelKey="status" />
              </Grid>
              <Grid item xs={12} md={6}>
                <DataBreakdown title="By Category" data={reportData.by_category} labelKey="category__name" />
              </Grid>
            </Grid>
          </>
        )

      case 'maintenance_summary':
        return (
          <>
            <Grid container spacing={2} mb={3}>
              <Grid item xs={6} sm={3}>
                <StatBox label="Total Work Orders" value={reportData.total_work_orders} color="#1B5E7A" />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatBox
                  label="Completed"
                  value={reportData.by_status?.find((s) => s.status === 'completed')?.count || 0}
                  color="#10b981"
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatBox
                  label="In Progress"
                  value={reportData.by_status?.find((s) => s.status === 'in_progress')?.count || 0}
                  color="#3b82f6"
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatBox
                  label="Avg Hours"
                  value={reportData.avg_completion_hours?.toFixed(1) || '-'}
                  color="#8b5cf6"
                />
              </Grid>
            </Grid>
            <Divider />
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <DataBreakdown title="By Status" data={reportData.by_status} labelKey="status" />
              </Grid>
              <Grid item xs={12} md={6}>
                <DataBreakdown title="By Priority" data={reportData.by_priority} labelKey="priority" />
              </Grid>
            </Grid>
          </>
        )

      case 'work_order_analysis':
        return (
          <>
            <Grid container spacing={2} mb={3}>
              <Grid item xs={6} sm={3}>
                <StatBox label="Total" value={reportData.total_work_orders} color="#1B5E7A" />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatBox label="Completed" value={reportData.completed} color="#10b981" />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatBox label="Completion Rate" value={`${reportData.completion_rate}%`} color="#3b82f6" />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatBox label="Period" value={`${reportData.period_days} days`} color="#8b5cf6" />
              </Grid>
            </Grid>
            <Divider />
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <DataBreakdown title="By Type" data={reportData.by_type} labelKey="work_type" />
              </Grid>
              <Grid item xs={12} md={4}>
                <DataBreakdown title="By Priority" data={reportData.by_priority} labelKey="priority" />
              </Grid>
              <Grid item xs={12} md={4}>
                <DataBreakdown title="By Status" data={reportData.by_status} labelKey="status" />
              </Grid>
            </Grid>
          </>
        )

      case 'inventory_summary':
        return (
          <>
            <Grid container spacing={2} mb={3}>
              <Grid item xs={12} sm={4}>
                <StatBox label="Total Parts" value={reportData.total_parts} color="#1B5E7A" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <StatBox label="Low Stock Items" value={reportData.low_stock_count} color="#ef4444" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <StatBox
                  label="Total Value"
                  value={`UGX ${(reportData.total_inventory_value || 0).toLocaleString()}`}
                  color="#10b981"
                />
              </Grid>
            </Grid>
            <Divider />
            <Box mt={3}>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                {reportData.low_stock_count > 0
                  ? `${reportData.low_stock_count} items need to be reordered`
                  : 'All inventory levels are adequate'}
              </Typography>
            </Box>
          </>
        )

      case 'cost_analysis':
        return (
          <>
            <Grid container spacing={2} mb={3}>
              <Grid item xs={12} sm={4}>
                <StatBox
                  label="Total Cost"
                  value={`UGX ${(reportData.total_maintenance_cost || 0).toLocaleString()}`}
                  color="#ef4444"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <StatBox
                  label="Average Cost"
                  value={`UGX ${(reportData.average_work_order_cost || 0).toLocaleString()}`}
                  color="#f59e0b"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <StatBox label="Period" value={`${reportData.period_days} days`} color="#1B5E7A" />
              </Grid>
            </Grid>
            <Divider />
            <DataBreakdown
              title="Cost by Work Type"
              data={reportData.by_work_type}
              labelKey="work_type"
              valueKey="total"
            />
          </>
        )

      case 'vendor_performance':
        return (
          <>
            <Grid container spacing={2} mb={3}>
              <Grid item xs={12} sm={4}>
                <StatBox label="Active Vendors" value={reportData.total_vendors} color="#1B5E7A" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <StatBox label="Active Contracts" value={reportData.active_contracts} color="#10b981" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <StatBox label="Expiring Soon" value={reportData.expiring_soon} color="#f59e0b" />
              </Grid>
            </Grid>
            <Divider />
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <DataBreakdown title="By Type" data={reportData.by_type} labelKey="vendor_type" />
              </Grid>
              <Grid item xs={12} md={6}>
                <Box mt={3}>
                  <Typography variant="subtitle2" fontWeight={600} mb={2}>
                    Top Vendors
                  </Typography>
                  {reportData.top_vendors?.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Vendor</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell align="center">Rating</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {reportData.top_vendors.map((vendor) => (
                            <TableRow key={vendor.id}>
                              <TableCell>{vendor.name}</TableCell>
                              <TableCell sx={{ textTransform: 'capitalize' }}>
                                {vendor.vendor_type?.replace(/_/g, ' ')}
                              </TableCell>
                              <TableCell align="center">{vendor.rating}/5</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No vendor data available
                    </Typography>
                  )}
                </Box>
              </Grid>
            </Grid>
          </>
        )

      // Specialized Reports
      case 'downtime_analysis':
        return (
          <>
            <Grid container spacing={2} mb={3}>
              <Grid item xs={6} sm={3}>
                <StatBox label="Total Downtime" value={`${reportData.summary?.total_downtime_hours?.toFixed(1) || 0}h`} color="#dc2626" />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatBox label="Incidents" value={reportData.summary?.total_incidents || 0} color="#1B5E7A" />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatBox label="Critical Downtime" value={`${reportData.summary?.critical_asset_downtime?.toFixed(1) || 0}h`} color="#f59e0b" />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatBox label="Critical Incidents" value={reportData.summary?.critical_incidents || 0} color="#ef4444" />
              </Grid>
            </Grid>
            <Divider />
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <DataBreakdown title="By Department" data={reportData.by_department} labelKey="department__name" valueKey="total_downtime" />
              </Grid>
              <Grid item xs={12} md={6}>
                <DataBreakdown title="By Category" data={reportData.by_category} labelKey="category" valueKey="total_downtime" />
              </Grid>
            </Grid>
          </>
        )

      case 'pm_compliance':
        return (
          <>
            <Grid container spacing={2} mb={3}>
              <Grid item xs={6} sm={3}>
                <StatBox label="Total PMs" value={reportData.summary?.total_scheduled || 0} color="#1B5E7A" />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatBox label="Completed" value={reportData.summary?.completed || 0} color="#10b981" />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatBox label="Compliance Rate" value={`${reportData.summary?.compliance_rate || 0}%`} color="#3b82f6" />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatBox label="Overdue" value={reportData.summary?.overdue || 0} color="#ef4444" />
              </Grid>
            </Grid>
            <Divider />
            <Box mt={3}>
              <Typography variant="subtitle2" fontWeight={600} mb={2}>By PM Template</Typography>
              {reportData.by_template?.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Template</TableCell>
                        <TableCell>Frequency</TableCell>
                        <TableCell align="center">Total</TableCell>
                        <TableCell align="center">Completed</TableCell>
                        <TableCell align="center">Overdue</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {reportData.by_template.map((item, i) => (
                        <TableRow key={i}>
                          <TableCell>{item.template__name || 'Unknown'}</TableCell>
                          <TableCell sx={{ textTransform: 'capitalize' }}>{item.template__frequency}</TableCell>
                          <TableCell align="center">{item.total}</TableCell>
                          <TableCell align="center">{item.completed}</TableCell>
                          <TableCell align="center" sx={{ color: item.overdue > 0 ? 'error.main' : 'inherit' }}>{item.overdue}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : <Typography variant="body2" color="text.secondary">No PM data available</Typography>}
            </Box>
          </>
        )

      case 'calibration_report':
        return (
          <>
            <Grid container spacing={2} mb={3}>
              <Grid item xs={6} sm={2}>
                <StatBox label="Total" value={reportData.summary?.total_calibrations || 0} color="#1B5E7A" />
              </Grid>
              <Grid item xs={6} sm={2}>
                <StatBox label="Passed" value={reportData.summary?.passed || 0} color="#10b981" />
              </Grid>
              <Grid item xs={6} sm={2}>
                <StatBox label="Failed" value={reportData.summary?.failed || 0} color="#ef4444" />
              </Grid>
              <Grid item xs={6} sm={2}>
                <StatBox label="Pass Rate" value={`${reportData.summary?.pass_rate || 0}%`} color="#3b82f6" />
              </Grid>
              <Grid item xs={6} sm={2}>
                <StatBox label="Overdue" value={reportData.summary?.overdue_count || 0} color="#f59e0b" />
              </Grid>
              <Grid item xs={6} sm={2}>
                <StatBox label="Due Soon" value={reportData.summary?.upcoming_30_days || 0} color="#8b5cf6" />
              </Grid>
            </Grid>
            <Divider />
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box mt={3}>
                  <Typography variant="subtitle2" fontWeight={600} mb={2}>Overdue Calibrations</Typography>
                  {reportData.overdue_calibrations?.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Asset</TableCell>
                            <TableCell>Category</TableCell>
                            <TableCell>Due Date</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {reportData.overdue_calibrations.slice(0, 10).map((item) => (
                            <TableRow key={item.id}>
                              <TableCell>{item.asset__name}</TableCell>
                              <TableCell>{item.asset__category__name}</TableCell>
                              <TableCell sx={{ color: 'error.main' }}>{item.next_calibration_date}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : <Typography variant="body2" color="text.secondary">No overdue calibrations</Typography>}
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box mt={3}>
                  <Typography variant="subtitle2" fontWeight={600} mb={2}>Upcoming Calibrations (30 days)</Typography>
                  {reportData.upcoming_calibrations?.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Asset</TableCell>
                            <TableCell>Category</TableCell>
                            <TableCell>Due Date</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {reportData.upcoming_calibrations.slice(0, 10).map((item) => (
                            <TableRow key={item.id}>
                              <TableCell>{item.asset__name}</TableCell>
                              <TableCell>{item.asset__category__name}</TableCell>
                              <TableCell>{item.next_calibration_date}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : <Typography variant="body2" color="text.secondary">No upcoming calibrations</Typography>}
                </Box>
              </Grid>
            </Grid>
          </>
        )

      case 'equipment_lifecycle':
        return (
          <>
            <Grid container spacing={2} mb={3}>
              <Grid item xs={12} sm={4}>
                <StatBox label="Total Assets" value={reportData.total_assets || 0} color="#1B5E7A" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <StatBox label="Needing Replacement" value={reportData.summary?.assets_needing_replacement || 0} color="#ef4444" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <StatBox label="Warranties Expiring" value={reportData.summary?.warranties_expiring_90_days || 0} color="#f59e0b" />
              </Grid>
            </Grid>
            <Divider />
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box mt={3}>
                  <Typography variant="subtitle2" fontWeight={600} mb={2}>Age Distribution</Typography>
                  {reportData.age_distribution && Object.entries(reportData.age_distribution).map(([range, count]) => (
                    <Box key={range} mb={1.5}>
                      <Box display="flex" justifyContent="space-between" mb={0.5}>
                        <Typography variant="body2">{range}</Typography>
                        <Typography variant="body2" fontWeight={500}>{count}</Typography>
                      </Box>
                      <LinearProgress variant="determinate" value={(count / (reportData.total_assets || 1)) * 100}
                        sx={{ height: 6, borderRadius: 1, backgroundColor: '#e2e8f0', '& .MuiLinearProgress-bar': { backgroundColor: '#1B5E7A' } }} />
                    </Box>
                  ))}
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box mt={3}>
                  <Typography variant="subtitle2" fontWeight={600} mb={2}>Replacement Forecast</Typography>
                  {reportData.replacement_forecast?.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Asset</TableCell>
                            <TableCell>Age</TableCell>
                            <TableCell>Remaining</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {reportData.replacement_forecast.slice(0, 8).map((item) => (
                            <TableRow key={item.id}>
                              <TableCell>{item.name}</TableCell>
                              <TableCell>{item.age_years}y</TableCell>
                              <TableCell sx={{ color: item.remaining_years < 1 ? 'error.main' : 'warning.main' }}>{item.remaining_years}y</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : <Typography variant="body2" color="text.secondary">No assets needing replacement</Typography>}
                </Box>
              </Grid>
            </Grid>
          </>
        )

      case 'departmental_costs':
        return (
          <>
            <Grid container spacing={2} mb={3}>
              <Grid item xs={12} sm={4}>
                <StatBox label="Total Parts Cost" value={`UGX ${(reportData.summary?.total_parts_cost || 0).toLocaleString()}`} color="#ef4444" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <StatBox label="Total Work Orders" value={reportData.summary?.total_work_orders || 0} color="#1B5E7A" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <StatBox label="Period" value={`${reportData.period_days} days`} color="#8b5cf6" />
              </Grid>
            </Grid>
            <Divider />
            <Box mt={3}>
              <Typography variant="subtitle2" fontWeight={600} mb={2}>Costs by Department</Typography>
              {reportData.departments?.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Department</TableCell>
                        <TableCell align="center">Assets</TableCell>
                        <TableCell align="center">Work Orders</TableCell>
                        <TableCell align="right">Parts Cost</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {reportData.departments.map((dept) => (
                        <TableRow key={dept.id}>
                          <TableCell>{dept.name}</TableCell>
                          <TableCell align="center">{dept.asset_count}</TableCell>
                          <TableCell align="center">{dept.work_order_count}</TableCell>
                          <TableCell align="right">UGX {(dept.parts_cost || 0).toLocaleString()}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : <Typography variant="body2" color="text.secondary">No departmental data available</Typography>}
            </Box>
          </>
        )

      case 'high_cost_assets':
        return (
          <>
            <Grid container spacing={2} mb={3}>
              <Grid item xs={12}>
                <StatBox label="Total Asset Investment" value={`UGX ${(reportData.total_asset_investment || 0).toLocaleString()}`} color="#1B5E7A" />
              </Grid>
            </Grid>
            <Divider />
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box mt={3}>
                  <Typography variant="subtitle2" fontWeight={600} mb={2}>Highest Purchase Cost</Typography>
                  {reportData.top_by_purchase_cost?.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Asset</TableCell>
                            <TableCell>Category</TableCell>
                            <TableCell align="right">Cost</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {reportData.top_by_purchase_cost.slice(0, 10).map((item) => (
                            <TableRow key={item.id}>
                              <TableCell>{item.name}</TableCell>
                              <TableCell>{item.category__name}</TableCell>
                              <TableCell align="right">UGX {(item.purchase_cost || 0).toLocaleString()}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : <Typography variant="body2" color="text.secondary">No data available</Typography>}
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box mt={3}>
                  <Typography variant="subtitle2" fontWeight={600} mb={2}>Highest Maintenance Cost</Typography>
                  {reportData.top_by_maintenance_cost?.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Asset</TableCell>
                            <TableCell align="center">WOs</TableCell>
                            <TableCell align="right">Cost</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {reportData.top_by_maintenance_cost.slice(0, 10).map((item) => (
                            <TableRow key={item.id}>
                              <TableCell>{item.name}</TableCell>
                              <TableCell align="center">{item.work_order_count}</TableCell>
                              <TableCell align="right">UGX {(item.total_parts_cost || 0).toLocaleString()}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : <Typography variant="body2" color="text.secondary">No data available</Typography>}
                </Box>
              </Grid>
            </Grid>
          </>
        )

      // Radiation Oncology Reports
      case 'qa_compliance':
        return (
          <>
            <Grid container spacing={2} mb={3}>
              <Grid item xs={6} sm={2}>
                <StatBox label="Total QA" value={reportData.summary?.total_qa_records || 0} color="#1B5E7A" />
              </Grid>
              <Grid item xs={6} sm={2}>
                <StatBox label="Passed" value={reportData.summary?.passed || 0} color="#10b981" />
              </Grid>
              <Grid item xs={6} sm={2}>
                <StatBox label="Failed" value={reportData.summary?.failed || 0} color="#ef4444" />
              </Grid>
              <Grid item xs={6} sm={2}>
                <StatBox label="Conditional" value={reportData.summary?.conditional || 0} color="#f59e0b" />
              </Grid>
              <Grid item xs={6} sm={2}>
                <StatBox label="Pass Rate" value={`${reportData.summary?.pass_rate || 0}%`} color="#3b82f6" />
              </Grid>
              <Grid item xs={6} sm={2}>
                <StatBox label="Pending" value={reportData.summary?.pending_review || 0} color="#8b5cf6" />
              </Grid>
            </Grid>
            <Divider />
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <DataBreakdown title="By QA Type" data={reportData.by_type} labelKey="qa_type" valueKey="total" />
              </Grid>
              <Grid item xs={12} md={6}>
                <Box mt={3}>
                  <Typography variant="subtitle2" fontWeight={600} mb={2}>Failed Records</Typography>
                  {reportData.failed_records?.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Asset</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Date</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {reportData.failed_records.map((item) => (
                            <TableRow key={item.id}>
                              <TableCell>{item.asset__name}</TableCell>
                              <TableCell sx={{ textTransform: 'capitalize' }}>{item.qa_type?.replace('_', ' ')}</TableCell>
                              <TableCell>{item.qa_date}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : <Typography variant="body2" color="text.secondary">No failed QA records</Typography>}
                </Box>
              </Grid>
            </Grid>
          </>
        )

      case 'radiation_source_inventory':
        return (
          <>
            <Grid container spacing={2} mb={3}>
              <Grid item xs={6} sm={3}>
                <StatBox label="Active Sources" value={reportData.summary?.total_active_sources || 0} color="#1B5E7A" />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatBox label="Initial Activity" value={`${reportData.summary?.total_initial_activity_gbq || 0} GBq`} color="#10b981" />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatBox label="Current Activity" value={`${reportData.summary?.total_current_activity_gbq || 0} GBq`} color="#3b82f6" />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatBox label="Leak Tests Overdue" value={reportData.summary?.leak_tests_overdue || 0} color="#ef4444" />
              </Grid>
            </Grid>
            <Divider />
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <DataBreakdown title="By Isotope" data={reportData.by_isotope} labelKey="isotope" valueKey="count" />
              </Grid>
              <Grid item xs={12} md={6}>
                <Box mt={3}>
                  <Typography variant="subtitle2" fontWeight={600} mb={2}>Leak Test Due</Typography>
                  {reportData.leak_test_due?.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Source ID</TableCell>
                            <TableCell>Isotope</TableCell>
                            <TableCell>Last Test</TableCell>
                            <TableCell>Days Ago</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {reportData.leak_test_due.map((item) => (
                            <TableRow key={item.id}>
                              <TableCell>{item.source_id}</TableCell>
                              <TableCell>{item.isotope}</TableCell>
                              <TableCell>{item.last_leak_test || 'Never'}</TableCell>
                              <TableCell sx={{ color: 'error.main' }}>{item.days_since_leak_test || 'N/A'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : <Typography variant="body2" color="text.secondary">No overdue leak tests</Typography>}
                </Box>
              </Grid>
            </Grid>
          </>
        )

      case 'equipment_log_summary':
        return (
          <>
            <Grid container spacing={2} mb={3}>
              <Grid item xs={6} sm={2}>
                <StatBox label="Total Logs" value={reportData.summary?.total_logs || 0} color="#1B5E7A" />
              </Grid>
              <Grid item xs={6} sm={2}>
                <StatBox label="Beam On" value={`${reportData.summary?.total_beam_on_hours || 0}h`} color="#10b981" />
              </Grid>
              <Grid item xs={6} sm={2}>
                <StatBox label="Downtime" value={`${reportData.summary?.total_downtime_hours || 0}h`} color="#ef4444" />
              </Grid>
              <Grid item xs={6} sm={2}>
                <StatBox label="Availability" value={`${reportData.summary?.availability_rate || 0}%`} color="#3b82f6" />
              </Grid>
              <Grid item xs={6} sm={2}>
                <StatBox label="Interruptions" value={reportData.summary?.treatment_interruptions || 0} color="#f59e0b" />
              </Grid>
              <Grid item xs={6} sm={2}>
                <StatBox label="Affected" value={reportData.summary?.treatments_affected || 0} color="#8b5cf6" />
              </Grid>
            </Grid>
            <Divider />
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <DataBreakdown title="By Log Type" data={reportData.by_type} labelKey="log_type" valueKey="count" />
              </Grid>
              <Grid item xs={12} md={6}>
                <Box mt={3}>
                  <Typography variant="subtitle2" fontWeight={600} mb={2}>Error Logs</Typography>
                  {reportData.error_logs?.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Asset</TableCell>
                            <TableCell>Date</TableCell>
                            <TableCell>Downtime</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {reportData.error_logs.map((item) => (
                            <TableRow key={item.id}>
                              <TableCell>{item.asset__name}</TableCell>
                              <TableCell>{new Date(item.log_date).toLocaleDateString()}</TableCell>
                              <TableCell>{item.downtime_minutes}m</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : <Typography variant="body2" color="text.secondary">No error logs</Typography>}
                </Box>
              </Grid>
            </Grid>
          </>
        )

      default:
        return (
          <Box>
            <pre style={{ whiteSpace: 'pre-wrap', fontSize: '12px' }}>{JSON.stringify(reportData, null, 2)}</pre>
          </Box>
        )
    }
  }

  const reportCategories = [
    { key: 'standard', label: 'Standard Reports' },
    { key: 'specialized', label: 'Specialized Reports' },
    { key: 'radiation', label: 'Radiation Oncology' },
  ]

  const reportsWithDaysPeriod = [
    'maintenance_summary', 'work_order_analysis', 'cost_analysis',
    'downtime_analysis', 'pm_compliance', 'calibration_report',
    'departmental_costs', 'qa_compliance', 'equipment_log_summary'
  ]

  return (
    <Box>
      <PageHeader
        title="Reports"
        subtitle="Generate and view maintenance reports"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Reports' },
        ]}
      />

      {reportCategories.map((category) => {
        const categoryReports = reportTypes.filter((r) => r.category === category.key)
        if (categoryReports.length === 0) return null

        return (
          <Box key={category.key} mb={4}>
            <Typography variant="h6" fontWeight={600} mb={2} color="text.secondary">
              {category.label}
            </Typography>
            <Grid container spacing={2}>
              {categoryReports.map((report) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={report.id}>
                  <Card
                    sx={{
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      border: selectedReport?.id === report.id ? `2px solid ${report.color}` : '1px solid #e2e8f0',
                      height: '100%',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: 2,
                      },
                    }}
                    onClick={() => handleGenerateReport(report)}
                  >
                    <Box display="flex" alignItems="flex-start" gap={1.5}>
                      <Box
                        sx={{
                          backgroundColor: `${report.color}15`,
                          color: report.color,
                          p: 1,
                          borderRadius: 1.5,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {report.icon}
                      </Box>
                      <Box flex={1}>
                        <Typography variant="subtitle2" fontWeight={600}>
                          {report.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          {report.description}
                        </Typography>
                      </Box>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )
      })}

      {selectedReport && (
        <Card sx={{ mt: 3 }}>
          <Box
            display="flex"
            flexDirection={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', sm: 'center' }}
            gap={2}
            mb={3}
          >
            <Typography variant="h6" fontWeight={600}>
              {selectedReport.title}
            </Typography>
            <Box display="flex" gap={2} flexDirection={{ xs: 'column', sm: 'row' }}>
              {reportsWithDaysPeriod.includes(selectedReport.id) && (
                <TextField
                  select
                  size="small"
                  label="Period"
                  value={days}
                  onChange={(e) => setDays(e.target.value)}
                  sx={{ minWidth: 120 }}
                >
                  <MenuItem value={7}>Last 7 days</MenuItem>
                  <MenuItem value={30}>Last 30 days</MenuItem>
                  <MenuItem value={90}>Last 90 days</MenuItem>
                  <MenuItem value={365}>Last year</MenuItem>
                </TextField>
              )}
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={() => handleGenerateReport(selectedReport)}
                disabled={loading}
              >
                Refresh
              </Button>
              <Button
                startIcon={<Download />}
                onClick={handleExportCSV}
                disabled={loading || !reportData}
              >
                Export CSV
              </Button>
            </Box>
          </Box>
          <Divider sx={{ mb: 3 }} />
          {renderReportContent()}
        </Card>
      )}
    </Box>
  )
}

export default ReportsList
